/**
 * The owner's three: rename the group, fix its currency before anything has
 * been spent in it, and archive it once it is done with.
 *
 * `server/groups.ts`'s PATCH route is the authority on all three —
 * `requireOwner` refuses anybody else, and `currency_locked` refuses a
 * currency change once there is a transaction. This panel only saves a round
 * trip on the second: the currency field disables itself once this screen has
 * already seen an expense, using the same list the expense feed reads,
 * rather than sending a request just to be told no.
 *
 * `Group.tsx` renders this only for the owner. There is no control here worth
 * showing a member who cannot press it.
 */

import { useState, type FormEvent } from 'react'
import { MAX_GROUP_NAME, readGroupDraft } from '../lib/group-draft'
import { useUpdateGroupSettings } from '../queries'

export function GroupSettings({
  groupId,
  name,
  currency,
  archivedAt,
  hasTransactions,
}: {
  groupId: string
  name: string
  currency: string
  archivedAt: string | null
  hasTransactions: boolean
}) {
  const [open, setOpen] = useState(false)
  const [draftName, setDraftName] = useState(name)
  const [draftCurrency, setDraftCurrency] = useState(currency)
  const [problem, setProblem] = useState<string | null>(null)
  const update = useUpdateGroupSettings(groupId)
  const archived = archivedAt !== null

  const save = (event: FormEvent) => {
    event.preventDefault()
    const reading = readGroupDraft(draftName, draftCurrency)
    if (!reading.ok) {
      setProblem(reading.problem)
      return
    }
    setProblem(null)
    update.mutate(reading.draft)
  }

  return (
    <div className="people-panel">
      <p className="actions">
        <button
          type="button"
          className="button--quiet"
          onClick={() => {
            setDraftName(name)
            setDraftCurrency(currency)
            setProblem(null)
            setOpen(!open)
          }}
        >
          {open ? 'Hide settings' : 'Settings'}
        </button>
      </p>

      {open && (
        <>
          <form className="form" onSubmit={save}>
            <div className="form__pair">
              <p className="form__row">
                <label htmlFor="group-settings-name">Name</label>
                <input
                  id="group-settings-name"
                  maxLength={MAX_GROUP_NAME}
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                />
              </p>
              <p className="form__row">
                <label htmlFor="group-settings-currency">Currency</label>
                <input
                  id="group-settings-currency"
                  maxLength={3}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={hasTransactions}
                  value={draftCurrency}
                  onChange={(event) => setDraftCurrency(event.target.value.toUpperCase())}
                />
              </p>
            </div>

            {hasTransactions && (
              <p className="muted note">
                This group already has expenses in {currency}, so its currency cannot change.
              </p>
            )}
            {problem && <p className="bad note">{problem}</p>}
            {update.isError && <p className="bad note">{update.error.message}</p>}

            <p className="actions">
              <button type="submit" disabled={update.isPending || draftName.trim().length === 0}>
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
            </p>
          </form>

          <p className="actions">
            <button
              type="button"
              className={archived ? 'button--quiet' : 'button--danger'}
              disabled={update.isPending}
              onClick={() => update.mutate({ archived: !archived })}
            >
              {archived ? 'Unarchive this group' : 'Archive this group'}
            </button>
          </p>
          <p className="muted note">
            {archived
              ? 'It takes no new expenses while archived, and its money still counts. Unarchiving lets it take them again.'
              : 'Archiving stops new expenses, invites and receipts. It can be undone.'}
          </p>
        </>
      )}
    </div>
  )
}
